import './Game.css';

// Vendors
import React, {Component} from 'react';
import classnames from 'classnames';
import Anime from 'react-anime';
import { CSSTransition } from 'react-transition-group';
import cloneDeep from 'lodash.clonedeep';

// Constats
import STATUS from './Constants';

// Data
import initialCards from './GameData';

// Components
import Card from '../Card/Card';
import Counter from './Counter/Counter';
import Match from './Match/Match';
import Endframe from './EndFrame/EndFrame';
import Logo from './Logo/Logo';
import Instruction, {Container as InstructionsContainer} from '../../shared/Instruction';

export default class Game extends Component {
  constructor(props) {
    super(props);

    this.defaultOptions = {
      activeId: null, // ID of the selected card
      lastCard: null, // last card object
      matches: 0, // count the number of matches
      locked: true, // set to true to disable card interaction
      finalMatch: false, // update message if it's the final match
      showMatch: null, // used to show the matching cards alert
      bgCards: true, // used for cards enter animation
      end: false, // used for cards end animation
      start: false, // used for cards start animation
      status: STATUS.LOGO,
      instructionStep: 0,
    }

    this.state = Object.assign({}, this.defaultOptions, {
      cards: this.shuffle(initialCards()),
      cardsCount: initialCards().length / 2,
    });
  }

  // Restart the game
  startGame() {
    // Reset state
    const mergeSettings = Object.assign({}, this.defaultOptions, {
      cards:this.shuffle(initialCards()),
      cardsCount: initialCards().length / 2,
      status: STATUS.GAME_START, // start the game
    });

    this.setState(mergeSettings);
  }

  // Play the intro
  playIntro() {
    this.setState(Object.assign({}, this.defaultOptions, {
      status: STATUS.INTRO,
      cards: this.shuffle(initialCards()),
      cardsCount: initialCards().length / 2,
      instructionStep: 0,
    }));
  }

  // Check matching pairs
  checkMatch(value, id) {
    if (this.state.locked || this.state.cards[id].matched) {
      return;
    }

    // Check latest states for the cards
    const cards = this.state.cards;

    // Flip current card
    cards[id].flipped = true;

    // Update cards
    this.setState({
      cards,
      locked: true,
      activeId: id, // ID of the selected card
    });

    // If first card of a pair is flipped
    if (!this.state.lastCard) {
      this.setState({
        lastCard: {
          id,
          value
        },
        locked: false,
        showMatch: false
      })
    } else {
      // Second card of the pair is flipped
      // If cards match
      if (value === this.state.lastCard.value) {
        // Increment match count
        const matches = this.state.matches + 1;

        // Set matched value for both cards to true
        cards[id].matched = true;
        cards[this.state.lastCard.id].matched = true;

        // Update state of the game
        // Alert correct match
        setTimeout(() => {
          this.setState({
            cards,
            showMatch: true,
            matches: matches,
            finalMatch: matches === this.state.cardsCount
          })
        }, 300);

      } else {
        // Cards don't match
        // Alert incorrect match
        const shakeTimeout = 500;
        const flipTimeout = 750;
        const cards = this.state.cards;

        // wait for the card to flip before shaking
        setTimeout(() => {
          cards[id].shake = true;
          cards[this.state.lastCard.id].shake = true;
          this.setState(cards);

          // wait for the card to shake before flipping
          setTimeout(() => {
            // Reset both cards
            cards[id].flipped = false;
            cards[this.state.lastCard.id].flipped = false;
            cards[id].shake = false;
            cards[this.state.lastCard.id].shake = false;

            // Update state
            this.setState({
              cards,
              lastCard: null,
              locked: false
            });
          }, flipTimeout);
        }, shakeTimeout);
      }
    }
  }

  flipBack(id) {
    if (this.state.locked || this.state.cards[id].matched) {
      return;
    }

    // Check latest states for the cards
    const cards = this.state.cards;

    // Flip current card
    cards[id].flipped = false;

    // Update cards
    this.setState({
      cards,
      locked: false,
      activeId: null, // ID of the selected card
      lastCard: null,
      showMatch: false,
    });
  }

  // Render playing cards
  renderCards() {
    return this.state.cards.map((card, index) => {
      let classes = classnames(
        'grid__col col-1 card-container',
        {'animated shake' : card.shake},
        {'card-intro' : this.state.status === STATUS.INTRO},
      )

      return (
        <div key={index} className={classes}>
          <Card
            value={card.value}
            id={index}
            type="playing"
            matched={card.matched}
            flipped={card.flipped}
            fold={card.fold}
            flipAnimate={card.flipAnimate}
            flipAnimateReverse={card.flipAnimateReverse}
            checkMatch={this.checkMatch.bind(this)}
            flipBack={this.flipBack.bind(this)}
            onAnimationEndCallback={this.onAnimationEndCallback.bind(this)}
          />
        </div>
      )
    })
  }

  // Render background for each card
  renderBgCards(cards) {
    return cards.map((card, index) => {
      return (
        <div key={index} className="grid__col col-1">
          <Card type="bg"/>
        </div>
      )
    })
  }

  // Render deck of cards
  // Animate in on game start
  renderPlayingCards() {
    if (this.state.status === STATUS.GAME_START) {
      return (
        // Slide cards in
        <Anime easing="easeOutExpo"
          duration={1000}
          delay={(el, index) => index * 100}
          translateX={['100vw', '0vw']}
          opacity={[0,1]}
          complete={()=>{
            this.setState({
              locked: false,
              status: STATUS.GAME_PLAY
            })
          }}>
          {this.renderCards()}
        </Anime>
      )
    } else if (this.state.status === STATUS.INTRO) {
      return (
        // Flip cards on enter
        <Anime easing="easeOutQuad"
          duration={600}
          delay={(el, index) => index * 75}
          translateX={['100vw', '0vw']}
          rotateY={['180deg', '160deg', '0deg']}
          complete={()=> {
            this.introCallback();
          }}
          >
          {this.renderCards()}
        </Anime>
      )
    } else if (this.state.status === STATUS.INTRO_END) {
      return (
        // Slide cards out
        <Anime easing="easeOutQuad"
          duration={600}
          delay={(el, index) => index * 150}
          translateX={['0vw', '-100vw']}
        >
          {this.renderCards()}
        </Anime>
      )
    } else {
      return(
        // Render playing cards
        // without animation
        this.renderCards()
      )
    }
  }

  // Render outline background
  renderBackgroundCards() {
    if (this.state.status === STATUS.GAME_START || this.state.status === STATUS.GAME_PLAY) {
      return (
        <div className="grid flex flex--wrap flex--justify-c grid--deck">
          {this.renderBgCards(this.state.cards)}
        </div>
      )
    }
  }

  // Shuffle cards
  shuffle(cards) {
    const newCards = [...cards];
    for (let i = newCards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newCards[i], newCards[j]] = [newCards[j], newCards[i]];
    }
    return newCards;
  }

  getRandomInt(max) {
    return Math.floor(Math.random() * Math.floor(max));
  }

  // Callback for intro
  introCallback() {
    this.setState({
      instructionStep: 1,
      status: STATUS.INTRO_BAD_PAIR,
    }, () => {
      setTimeout(() => {
        // Select 2 non-matching cards
        const cards = cloneDeep(this.state.cards);
        const pickedCards = [cards[this.getRandomInt(this.state.cardsCount-1)]];
        const nonMatchingCandidates = cards.filter((card) => card.value !== pickedCards[0].value);
        pickedCards.push(nonMatchingCandidates[this.getRandomInt(nonMatchingCandidates.length - 1)]);

        // Flip both picked cards, and no others
        for (const card of cards) {
          card.matched = card.flipped = card.flipAnimate = false;
        }
        for (const card of pickedCards) {
          card.flipped = card.flipAnimate = true;
        }

        this.setState({
          cards,
        })
      }, 1.5e3);
    });
  }

  // Animation End on Cards callback
  onAnimationEndCallback(event) {
    switch (event.animationName) {
      case 'flipAnimate':
      case 'revealIe':
        if (this.state.status !== STATUS.INTRO_BAD_PAIR && this.state.status !== STATUS.INTRO_GOOD_PAIR && this.state.status !== STATUS.GAME_PLAY) {
          return;
        }

        // Add a bit of delay before showing the match
        this.setState((prevState) => {
          if (prevState.status === STATUS.INTRO_BAD_PAIR) {
            // Tutorial bad pair revealed
            setTimeout(() => {
              const cards = cloneDeep(prevState.cards);
              for (const card of cards) {
                if (card.flipped) {
                  card.shake = true;
                }
              }
              setTimeout(() => {
                // After shake
                this.setState((prevState) => {
                  const cards = cloneDeep(prevState.cards);
                  for (const card of cards) {
                    if (card.flipped) {
                      card.flipped = false;
                      card.shake = false;
                      card.flipAnimate = false;
                      card.flipAnimateReverse = true;
                    }
                  }
                  return {
                    cards: cards,
                  };
                })
              }, 750);
              this.setState({
                cards: cards,
              })
            }, 1e3);
            return {
              instructionStep: 2,
            }
          }

          // Tutorial match revealed
          return {
            showMatch: true,
          };
        });
        break;

      case 'flipAnimateReverse':
      case 'unrevealIe':
        if (this.state.status !== STATUS.INTRO_BAD_PAIR) {
          return;
        }

        this.setState({
          instructionStep: 3,
        }, () => {
          setTimeout(() => {
            // Select 2 matching cards
            const cards = cloneDeep(this.state.cards);
            const randomNum = this.getRandomInt(this.state.cardsCount-1);
            const pickedCard = cards[randomNum]; // pick a card

            // Find the match of the picked card and set the flip prop to true
            for (const card of cards) {
              // Mark 2 matching cards
              // Flip these 2 cards
              card.matched = card.flipped = card.flipAnimate = card.value === pickedCard.value;
              card.flipAnimateReverse = false;
            }

            this.setState({
              cards,
              status: STATUS.INTRO_GOOD_PAIR,
            })
          }, 1e3)
        })
        break;

      default:
        break;
    }
  }

  // Match alert callback
  matchCallback() {
    if (this.state.status === STATUS.INTRO_GOOD_PAIR) {
      // Flip the matching cards back
      // and fade out the background cards
      const cards = this.state.cards;
      for (const card of cards) {
        card.flipAnimateReverse = card.flipped;
        card.matched = card.flipped = card.flipAnimate = false;
      }
      this.setState({
        instructionStep: 4,
      }, () => {
        setTimeout(() => {
          this.setState({
            cards: cards,
            status: STATUS.INTRO_END,
            showMatch: false,
          }, () => {
            // Show the logo after a delay
            setTimeout(() => {
              this.setState({
                status: STATUS.LOGO,
                showMatch: false,
              });
            }, 3000);
          })
        }, 1e3)
      })
    } else if (this.state.status === STATUS.GAME_PLAY) {
      // Match alert callback
      // on game match
      const id = this.state.activeId;
      const cards = this.state.cards;

      // Fold the cards
      cards[id].fold = true;
      cards[this.state.lastCard.id].fold = true;
      cards[id].flipped = false;
      cards[this.state.lastCard.id].flipped = false;

      this.setState({
        cards,
        lastCard: null,
        locked: false,
        showMatch: false,
        status: this.state.finalMatch ? STATUS.GAME_END : this.state.status,
      })
    }
  }

  // Render
  render() {
    const introPlaying = this.state.status === STATUS.INTRO ||
      this.state.status === STATUS.INTRO_BAD_PAIR ||
      this.state.status === STATUS.INTRO_GOOD_PAIR ||
      this.state.status === STATUS.INTRO_END;

    return (
      <div className={classnames('Game', introPlaying && 'introPlaying')}>
        {/* Logo */}
        <Logo status={this.state.status} startGame={this.startGame.bind(this)} playIntro={this.playIntro.bind(this)}/>

        {/* Render Game component */}
        <CSSTransition
          in={
            this.state.status === STATUS.INTRO ||
            this.state.status === STATUS.INTRO_BAD_PAIR ||
            this.state.status === STATUS.INTRO_GOOD_PAIR ||
            this.state.status === STATUS.INTRO_END ||
            this.state.status === STATUS.GAME_START ||
            this.state.status === STATUS.GAME_PLAY
          }
          timeout={1000}
          appear={true}
          exit={this.state.status !== STATUS.GAME_END && this.state.status !== STATUS.LOGO}
          unmountOnExit
          classNames="fade">

          <div className="wrapper">
            {/* The background for the deck */}
            <div className={classnames({blur: this.state.showMatch})}>
              {this.renderBackgroundCards()}

              {/* Render playing cards */}
              <div className="grid flex flex--wrap flex--justify-c">
                {this.renderPlayingCards()}
              </div>

              <Counter status={this.state.status} matches={this.state.matches} totalCards={this.state.cardsCount}/>
            </div>

            {(this.state.status === STATUS.INTRO || this.state.status === STATUS.INTRO_BAD_PAIR || this.state.status === STATUS.INTRO_GOOD_PAIR || this.state.status === STATUS.INTRO_END) && (
              <InstructionsContainer>
                <Instruction visible={this.state.instructionStep === 1} wasVisible={this.state.instructionStep > 1}>
                  Turn over any two cards and hope for a match.
                </Instruction>
                <Instruction visible={this.state.instructionStep === 2} wasVisible={this.state.instructionStep > 2}>
                  If the cards don't double up,
                </Instruction>
                <Instruction visible={this.state.instructionStep === 3} wasVisible={this.state.instructionStep > 3}>
                  try and find a combination of two cards that do.
                </Instruction>
                <Instruction visible={this.state.instructionStep === 4} wasVisible={this.state.instructionStep > 4}>
                  The game ends when all the cards have been matched.
                </Instruction>
              </InstructionsContainer>
            )}
          </div>
        </CSSTransition>

        {/* Render Matched Pairs component */}
        <Match
          status={this.state.status}
          showMatch={this.state.showMatch} // to whether show match or not
          cards={this.state.cards} // show matched cards
          finalMatch={this.state.finalMatch} // to show appropriate message
          callback={this.matchCallback.bind(this)} // callback after showing matching cards
        />

        {/* Render end frame */}
        <Endframe status={this.state.status} callback={this.startGame.bind(this)}/>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/du/Game/Game.js